"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
interface FaqBullet {
  id: string;
  title: string;
  description: string;
}
const uniqueBullets: FaqBullet[] = [{
  id: "bullet-1",
  title: "We Are Family Harmony Mediators:",
  description: "Managing extended family travel requires balancing diverse budgets, dietary needs, mobility limits, and sleep schedules. We act as objective advisors so you can remain a guest on your own vacation."
}, {
  id: "bullet-2",
  title: "Two Advisors, Zero Extra Cost:",
  description: "You receive the dedicated expertise of co-founders managing both the structural logistics and the creative experience simultaneously."
}, {
  id: "bullet-3",
  title: "VIP Preferred Status:",
  description: "Because of our preferred network affiliations, you receive exclusive amenities and priority treatment that direct online booking platforms cannot provide."
}, {
  id: "bullet-4",
  title: "Uncompromising Operational Rigor:",
  description: "From room allocation to emergency contingency plans, our backgrounds ensure institutional-level precision across every reservation."
}];
export default function Faq() {
  const prefersReducedMotion = useReducedMotion();
  return <section id="faq" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-t border-border/40">
      <div className="container mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <motion.div initial={prefersReducedMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.3
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }} className="text-center mb-12 md:mb-16">
          <span className="text-xs font-semibold tracking-widest uppercase text-primary block mb-3">
            Questions &amp; Clarifications
          </span>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-normal text-foreground tracking-tight">
            Frequently Asked Questions
          </h2>
        </motion.div>

        <motion.div initial={prefersReducedMotion ? {
        opacity: 1
      } : {
        opacity: 0,
        y: 12
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.5,
        delay: 0.1,
        ease: "easeOut"
      }} className="rounded-lg border border-border bg-card/60 px-6 sm:px-8 shadow-xs">
          <Accordion type="single" collapsible className="w-full divide-y divide-border">
            <AccordionItem value="item-1" className="border-b-0 py-2">
              <AccordionTrigger className="text-left font-serif text-lg sm:text-xl font-normal text-foreground hover:no-underline hover:text-primary py-5">
                What Makes Us Unique (Why Choose Us?)
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground pb-6">
                <ul className="space-y-4 pt-2">
                  {uniqueBullets.map((bullet, i) => <li key={bullet.id} data-index={i} className="flex items-start gap-2.5 text-sm sm:text-base leading-relaxed">
                      <span className="inline-block h-1.5 w-1.5 rounded-full bg-primary mt-2 shrink-0" />
                      <span>
                        <strong className="font-semibold text-foreground">
                          {bullet.title}{" "}
                        </strong>
                        {bullet.description}
                      </span>
                    </li>)}
                </ul>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-2" className="border-b-0 py-2">
              <AccordionTrigger className="text-left font-serif text-lg sm:text-xl font-normal text-foreground hover:no-underline hover:text-primary py-5">
                Why book through E•Leete Travel Design instead of directly with a hotel or cruise line?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground text-sm sm:text-base leading-relaxed pb-6 pt-1">
                Booking directly costs the exact same—or more—without any added benefits. Through our luxury network partnerships, we attach preferred VIP perks (upgrades, daily breakfast, resort credits) and provide dedicated advocate support if flight delays or disruptions occur.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-3" className="border-b-0 py-2">
              <AccordionTrigger className="text-left font-serif text-lg sm:text-xl font-normal text-foreground hover:no-underline hover:text-primary py-5">
                How do you handle different budgets and preferences within one extended family?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground text-sm sm:text-base leading-relaxed pb-6 pt-1">
                We utilize our structured intake survey to gather dietary needs, activity levels, and financial boundaries confidentially upfront. Our expense-tracking systems and parallel tracks allow family members to participate at their comfort level without awkwardness.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-4" className="border-b-0 py-2">
              <AccordionTrigger className="text-left font-serif text-lg sm:text-xl font-normal text-foreground hover:no-underline hover:text-primary py-5">
                What size groups do you specialize in?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground text-sm sm:text-base leading-relaxed pb-6 pt-1">
                We cater primarily to multi-generational family groups of 6 to 25+ travelers, including grandparents, adult siblings, parents, teens, and young children.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </motion.div>
      </div>
    </section>;
}